// src/features/leases/CreateLeaseModal.jsx
//
// LSE-LEASE-TAB-DRAFT-CREATION — modal for creating a new draft lease.
// Rendered by LeaseSummaryTab when no lease exists and canWrite is true.
//
// Key differences from EditLeaseModal:
//   - All fields are optional — only location_id is required (from context).
//   - On save, calls addLease(locationId, { status, ...fields }).
//   - On success, calls onSaved() (triggers LeaseSummaryTab refetch) and closes.
//   - No dirty-form confirm guard (it's a create, not an edit).
//   - No year-costs table (no lease ID exists until after save).
//   - No discard overlay (form is always blank on open; cancelling is safe).
//
// LEASES-ADD-BUTTON: when locationId is omitted the modal runs in "landing mode".
//   • Landing mode: shows a Location selector as the first field (populated from
//     GET /api/locations) and an editable Status select defaulting to in_negotiation.
//   • LeaseSummaryTab mode (locationId provided): status is fixed as read-only
//     "Draft" label — no change from the original flow.
//   • A 409 ConflictError from the server is surfaced as an inline friendly
//     message rather than a raw toast in both modes.

import React, { useEffect, useRef, useState } from "react";
import { Modal } from "../../components/Modal.jsx";
import { T } from "../../app/tokens.js";
import { DateInput } from "../../components/DateInput.jsx";
import { addLease } from "../../data/leasesClient.js";
import { getLocations } from "../../data/locationsClient.js";
import { LEASE_TYPE_OPTIONS } from "./leaseTypes.js";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { log } from "../../utils/logger.js";
import { showToast } from "../../components/toastService.jsx";

const TITLE_ID = "create-lease-modal-title";

// ─── Style constants (mirror EditLeaseModal) ──────────────────────────────

const FIELD_ROW_STYLE = {
  display: "grid",
  gridTemplateColumns: "150px 1fr",
  alignItems: "start",
  gap: 10,
  padding: "8px 0",
  borderBottom: `1px solid ${T.border}`,
};

const LABEL_STYLE = {
  fontSize: T.fsSm,
  color: T.mid,
  fontWeight: T.fwSemibold,
  paddingTop: 6,
};

const INPUT_BASE = {
  fontSize: T.fsBase,
  padding: "5px 8px",
  border: `1px solid ${T.borderDark}`,
  borderRadius: 4,
  width: "100%",
  fontFamily: "inherit",
  background: T.white,
  color: T.dark,
  boxSizing: "border-box",
};

// ─── Small presentational helpers ────────────────────────────────────────

function SectionBar({ title, collapsible = false, expanded, onToggle }) {
  const content = (
    <PaneHeading
      style={{
        marginBottom: 4,
        padding: "8px 0",
        borderBottom: `2px solid ${T.border}`,
        cursor: collapsible ? "pointer" : "default",
        userSelect: "none",
      }}
      labelStyle={{ display: "flex", alignItems: "center", flex: 1 }}
    >
      {title}
      {collapsible && (
        <span style={{ marginLeft: "auto", fontSize: T.fsXs, color: T.mid }}>
          {expanded ? "▲" : "▼"}
        </span>
      )}
    </PaneHeading>
  );
  return collapsible
    ? <button type="button" onClick={onToggle} style={{ all: "unset", display: "block", width: "100%", cursor: "pointer" }}>{content}</button>
    : content;
}

function FRow({ label, children }) {
  return (
    <div style={FIELD_ROW_STYLE}>
      <span style={LABEL_STYLE}>{label}</span>
      <div>{children}</div>
    </div>
  );
}

function TxtInput({ value, onChange, placeholder, disabled }) {
  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      placeholder={placeholder ?? ""}
      disabled={disabled}
      style={{ ...INPUT_BASE, opacity: disabled ? 0.6 : 1 }}
    />
  );
}

function NumInput({ value, onChange, disabled, min }) {
  return (
    <input
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      disabled={disabled}
      min={min}
      style={{ ...INPUT_BASE, width: "100%", opacity: disabled ? 0.6 : 1 }}
    />
  );
}

function DtInput({ value, onChange, disabled }) {
  function toInputVal(v) {
    if (!v) return "";
    const s = String(v);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
  }
  return (
    <DateInput
      value={toInputVal(value)}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      style={{ ...INPUT_BASE, opacity: disabled ? 0.6 : 1 }}
    />
  );
}

function SelInput({ value, onChange, options, disabled }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      style={{ ...INPUT_BASE, opacity: disabled ? 0.6 : 1 }}
    >
      <option value=""> - Select - </option>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const l = typeof o === "string" ? o : o.label;
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

// ─── Empty form state ─────────────────────────────────────────────────────

const EMPTY_FORM = {
  type: null,
  size: null,
  areas: null,
  vendor: null,
  lessee: null,
  startDate: null,
  endDate: null,
  totalCost: null,
  currency: null,
  freeRentMonths: null,
  escalationType: null,
  opexStructure: null,
  breakDate: null,
  renewalNoticeMonths: null,
  renewalCount: null,
  rofrRofoNotes: null,
  expansionOptionNotes: null,
  // LEASE-FIELDS-ADD-DROP: holdoverPercent removed (column dropped in 0216)
  securityDepositAmount: null,
  permittedUse: null,
  subletConsentRequired: null,
  sqftMeasurementStandard: null,
  subletRecaptureRight: null,
  restorationObligations: null,
  subletProfitSharePercent: null,
  baseYear: null,
  baseYearOpex: null,
  baseYearTax: null,
  opexCapPercent: null,
  noticeAddress: null,
};

// Status options shown in landing-mode create form.
// Omits expired/terminated/holdover (not meaningful at creation).
const LANDING_STATUS_OPTIONS = [
  { value: "in_negotiation", label: "In Negotiation" },
  { value: "draft",          label: "Draft" },
  { value: "active",         label: "Active" },
];

// Friendly message shown when the server returns 409 (active-lease conflict).
const CONFLICT_MSG =
  "This location already has an active lease. Choose a different status (e.g. In Negotiation) or update the existing lease first.";

// ─── Main export ──────────────────────────────────────────────────────────

export function CreateLeaseModal({ isOpen, onClose, locationId, onSaved, onSavedAndOpen }) {
  // landingMode = true when no locationId is provided (opened from landing page).
  // In this mode a Location selector and editable Status field are rendered.
  const landingMode = locationId == null;

  const [form, setForm]               = useState(EMPTY_FORM);
  const [submitting, setSubmitting]   = useState(false);
  const [formError, setFormError]     = useState(null);
  const [expanded, setExpanded]       = useState({
    summary: true, financial: true,
    criticalDates: false, options: false, restrictions: false,
  });

  // Landing mode: location selector state
  const [locationOptions, setLocationOptions]   = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectedLocId, setSelectedLocId]       = useState(null);

  // Status: editable in landing mode (defaulting to in_negotiation); fixed
  // to "draft" in LeaseSummaryTab mode (unchanged behaviour).
  const [leaseStatus, setLeaseStatus] = useState(landingMode ? "in_negotiation" : "draft");

  const bodyRef = useRef(null);

  // Fetch locations when opened in landing mode.
  useEffect(() => {
    if (!isOpen || !landingMode) return undefined;
    let cancelled = false;
    setLocationsLoading(true);
    getLocations()
      .then((locs) => {
        if (cancelled) return;
        setLocationOptions(Array.isArray(locs) ? locs : []);
        setLocationsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("CreateLeaseModal: failed to fetch locations", { err: String(err) });
        setLocationsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, landingMode]);

  // Reset form on every open.
  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setFormError(null);
      setSelectedLocId(null);
      setLeaseStatus(landingMode ? "in_negotiation" : "draft");
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus first focusable element on open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const raf = requestAnimationFrame(() => {
      bodyRef.current?.querySelector("input,select,textarea")?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  function setField(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (formError) setFormError(null);
  }

  function toggle(key) {
    setExpanded((e) => ({ ...e, [key]: !e[key] }));
  }

  // Boolean ↔ select helpers
  const toSel   = (v) => (v == null ? null : (v ? "Yes" : "No"));
  const fromSel = (v) => (v == null || v === "" ? null : v === "Yes");

  async function handleSave({ open = false } = {}) {
    if (submitting) return;

    const effectiveLocId = landingMode ? selectedLocId : locationId;

    // Landing mode: location is required.
    if (landingMode && !effectiveLocId) {
      setFormError("Please select a location.");
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const payload = { status: leaseStatus };
      for (const [k, v] of Object.entries(form)) {
        if (v != null) payload[k] = v;
      }
      const created = await addLease(effectiveLocId, payload);
      showToast("Lease added", { type: "success" });
      if (open && onSavedAndOpen) {
        onSavedAndOpen({ leaseId: created.id, locationId: effectiveLocId });
      } else {
        onSaved?.(created);
        onClose();
      }
    } catch (err) {
      log.error("CreateLeaseModal save failed", { locationId: effectiveLocId, err: String(err) });
      if (err?.name === "ConflictError" || err?.status === 409) {
        setFormError(CONFLICT_MSG);
      } else {
        setFormError(err?.message || "Save failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? undefined : onClose}
      ariaLabelledBy={TITLE_ID}
      testId="create-lease-modal"
      overlayTestId="create-lease-modal-overlay"
      maxWidth={680}
      disableBackdropClose
      title={landingMode ? "Add lease" : "Create draft lease"}
      subTitle="all fields optional"
    >

      {/* ── Body ───────────────────────────────────────────────── */}
      <div ref={bodyRef} data-testid="create-lease-modal-body" style={{ overflowY: "auto", flex: 1, padding: "12px 20px" }}>
        {formError && (
          <div role="alert" data-testid="create-lease-modal-form-error" style={{
            marginBottom: 12, padding: "8px 12px",
            background: T.redSoft, border: `1px solid ${T.redLight}`,
            borderRadius: 6, color: T.red, fontSize: T.fsSm, fontWeight: T.fwSemibold,
          }}>
            {formError}
          </div>
        )}

        {/* ─ LANDING MODE: Location selector (first field) ─────── */}
        {landingMode && (
          <div style={{ marginBottom: 16 }}>
            <SectionBar title="Location" />
            <FRow label="Location">
              <select
                data-testid="create-lease-location-select"
                value={selectedLocId ?? ""}
                onChange={(e) => {
                  setSelectedLocId(e.target.value || null);
                  if (formError) setFormError(null);
                }}
                disabled={submitting || locationsLoading}
                style={{ ...INPUT_BASE, opacity: (submitting || locationsLoading) ? 0.6 : 1 }}
              >
                <option value="">
                  {locationsLoading ? "Loading locations…" : "- Select location -"}
                </option>
                {locationOptions.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.market ? `${loc.id} — ${loc.market}` : loc.id}
                  </option>
                ))}
              </select>
            </FRow>
          </div>
        )}

        {/* ─ 1. LEASE SUMMARY (expanded) ────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <SectionBar title="Lease summary" />
          <FRow label="Type">
            <SelInput value={form.type} onChange={(v) => setField("type", v)}
              options={LEASE_TYPE_OPTIONS} disabled={submitting} />
          </FRow>
          <FRow label="Size (sf)">
            <NumInput value={form.size} onChange={(v) => setField("size", v)} disabled={submitting} min={0} />
          </FRow>
          <FRow label="Areas">
            <TxtInput value={form.areas} onChange={(v) => setField("areas", v)} disabled={submitting} />
          </FRow>
          <FRow label="Lessor">
            <TxtInput value={form.vendor} onChange={(v) => setField("vendor", v)} disabled={submitting} />
          </FRow>
          <FRow label="Lessee">
            <TxtInput value={form.lessee} onChange={(v) => setField("lessee", v)} disabled={submitting} />
          </FRow>
          <FRow label="Commencement">
            <DtInput value={form.startDate} onChange={(v) => setField("startDate", v)} disabled={submitting} />
          </FRow>
          <FRow label="End date">
            <DtInput value={form.endDate} onChange={(v) => setField("endDate", v)} disabled={submitting} />
          </FRow>
          <FRow label="Status">
            {landingMode ? (
              <SelInput
                value={leaseStatus}
                onChange={(v) => setLeaseStatus(v ?? "in_negotiation")}
                options={LANDING_STATUS_OPTIONS}
                disabled={submitting}
              />
            ) : (
              <span style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.amberDark, paddingTop: 5, display: "block" }}>
                Draft
              </span>
            )}
          </FRow>
        </div>

        {/* ─ 2. FINANCIAL TERMS (expanded) ──────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <SectionBar title="Financial terms" />
          <FRow label="Total cost (lease term)">
            <NumInput value={form.totalCost} onChange={(v) => setField("totalCost", v)} disabled={submitting} min={0} />
          </FRow>
          <FRow label="Currency">
            <TxtInput value={form.currency} onChange={(v) => setField("currency", v)} placeholder="USD" disabled={submitting} />
          </FRow>
          <FRow label="Free rent (months)">
            <NumInput value={form.freeRentMonths} onChange={(v) => setField("freeRentMonths", v)} disabled={submitting} min={0} />
          </FRow>
          <FRow label="Escalation">
            <SelInput value={form.escalationType} onChange={(v) => setField("escalationType", v)}
              options={["Fixed", "CPI", "None"]} disabled={submitting} />
          </FRow>
          <FRow label="OpEx structure">
            <SelInput value={form.opexStructure} onChange={(v) => setField("opexStructure", v)}
              options={["NNN", "Modified Gross", "Full Service Gross", "Gross"]} disabled={submitting} />
          </FRow>
        </div>

        {/* ─ 3. CRITICAL DATES (collapsed) ─────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <SectionBar title="Critical dates" collapsible expanded={expanded.criticalDates} onToggle={() => toggle("criticalDates")} />
          {expanded.criticalDates && (
            <>
              <FRow label="Break option date">
                <DtInput value={form.breakDate} onChange={(v) => setField("breakDate", v)} disabled={submitting} />
              </FRow>
              <FRow label="Renewal notice (months)">
                <NumInput value={form.renewalNoticeMonths} onChange={(v) => setField("renewalNoticeMonths", v)} disabled={submitting} min={0} />
              </FRow>
            </>
          )}
        </div>

        {/* ─ 4. OPTIONS (collapsed) ─────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <SectionBar title="Options" collapsible expanded={expanded.options} onToggle={() => toggle("options")} />
          {expanded.options && (
            <>
              <FRow label="Renewal options">
                <NumInput value={form.renewalCount} onChange={(v) => setField("renewalCount", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="ROFR / ROFO notes">
                <TxtInput value={form.rofrRofoNotes} onChange={(v) => setField("rofrRofoNotes", v)} disabled={submitting} />
              </FRow>
              <FRow label="Expansion option notes">
                <TxtInput value={form.expansionOptionNotes} onChange={(v) => setField("expansionOptionNotes", v)} disabled={submitting} />
              </FRow>
              {/* LEASE-FIELDS-ADD-DROP: Holdover % field removed (column dropped in 0216) */}
              <FRow label="Security deposit amount">
                <NumInput value={form.securityDepositAmount} onChange={(v) => setField("securityDepositAmount", v)} disabled={submitting} min={0} />
              </FRow>
            </>
          )}
        </div>

        {/* ─ 5. RESTRICTIONS (collapsed) ───────────────────── */}
        <div style={{ marginBottom: 8 }}>
          <SectionBar title="Restrictions & concessions" collapsible expanded={expanded.restrictions} onToggle={() => toggle("restrictions")} />
          {expanded.restrictions && (
            <>
              <FRow label="Permitted use">
                <TxtInput value={form.permittedUse} onChange={(v) => setField("permittedUse", v)} disabled={submitting} />
              </FRow>
              <FRow label="Sublet consent required">
                <SelInput
                  value={toSel(form.subletConsentRequired)}
                  onChange={(v) => setField("subletConsentRequired", fromSel(v))}
                  options={["Yes", "No"]} disabled={submitting}
                />
              </FRow>
              <FRow label="Sqft measurement std">
                <TxtInput value={form.sqftMeasurementStandard} onChange={(v) => setField("sqftMeasurementStandard", v)} disabled={submitting} />
              </FRow>
              <FRow label="Recapture right">
                <SelInput
                  value={toSel(form.subletRecaptureRight)}
                  onChange={(v) => setField("subletRecaptureRight", fromSel(v))}
                  options={["Yes", "No"]} disabled={submitting}
                />
              </FRow>
              <FRow label="Restoration obligations">
                <TxtInput value={form.restorationObligations} onChange={(v) => setField("restorationObligations", v)} disabled={submitting} />
              </FRow>
              <FRow label="Profit share %">
                <NumInput value={form.subletProfitSharePercent} onChange={(v) => setField("subletProfitSharePercent", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="Base year">
                <NumInput value={form.baseYear} onChange={(v) => setField("baseYear", v)} disabled={submitting} />
              </FRow>
              <FRow label="Base year OpEx">
                <NumInput value={form.baseYearOpex} onChange={(v) => setField("baseYearOpex", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="Base year tax">
                <NumInput value={form.baseYearTax} onChange={(v) => setField("baseYearTax", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="OpEx cap %">
                <NumInput value={form.opexCapPercent} onChange={(v) => setField("opexCapPercent", v)} disabled={submitting} min={0} />
              </FRow>
              <FRow label="Notice address">
                <TxtInput value={form.noticeAddress} onChange={(v) => setField("noticeAddress", v)} disabled={submitting} />
              </FRow>
            </>
          )}
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "flex-end",
        gap: 8, padding: "12px 20px", borderTop: `1px solid ${T.border}`, flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          data-testid="create-lease-modal-cancel"
          style={{
            background: "transparent", color: T.mid, border: `1px solid ${T.border}`,
            padding: "7px 14px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold,
            cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1,
          }}
        >Cancel</button>
        {onSavedAndOpen && (
          <button
            type="button"
            onClick={() => handleSave({ open: true })}
            disabled={submitting}
            data-testid="create-lease-modal-save-and-open"
            style={{
              background: "transparent", color: T.blue, border: `1px solid ${T.blue}`,
              padding: "7px 14px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwSemibold,
              cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1,
            }}
          >{submitting ? "Saving…" : "Save & open"}</button>
        )}
        <button
          type="button"
          onClick={() => handleSave()}
          disabled={submitting}
          data-testid="create-lease-modal-save"
          style={{
            background: T.blue, color: T.white, border: "none",
            padding: "7px 16px", borderRadius: 6, fontSize: T.fsSm5, fontWeight: T.fwBold,
            cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1,
          }}
        >{submitting ? "Saving…" : (landingMode ? "Save lease" : "Save draft")}</button>
      </div>
    </Modal>
  );
}
